import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  TableSortLabel,
  Button,
  CircularProgress
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar.tsx';
import { useAuth } from '../context/AuthContext.tsx';

const API_BASE = process.env.REACT_APP_API_URL + '/api';


interface Ronde {
  id: number;
  naam: string;
  beschrijving: string;
}

type SortField = 'naam' | 'beschrijving';
type SortDirection = 'asc' | 'desc';

const Rondes: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('naam');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  const [rondes, setRondes] = useState<Ronde[]>([]);

  useEffect(() => {
    document.title = 'Rondes - Thuisverpleging Portaal';
  }, []);

  useEffect(() => {
    const fetchRondes = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        const res = await fetch(`${API_BASE}/tours/simple`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!res.ok) {
          console.error('Failed to fetch rondes:', await res.text());
          return;
        }

        const data = await res.json();
        if (!Array.isArray(data)) {
          console.error('Unexpected rondes response:', data);
          return;
        }

        const mapped: Ronde[] = data.map((t: any) => ({
          id: t.id,
          naam: t.naam,
          beschrijving: t.beschrijving || ''
        }));

        setRondes(mapped);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchRondes();
    }
  }, [isAuthenticated]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleRondeClick = (ronde: Ronde) => {
    navigate(`/rondes/${ronde.id}`);
  };

  const filteredAndSortedData = rondes
    .filter((r) =>
      `${r.naam} ${r.beschrijving}`.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) =>
      sortDirection === 'asc'
        ? a[sortField].localeCompare(b[sortField])
        : b[sortField].localeCompare(a[sortField])
    );

  if (!isAuthenticated) return null;

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar open={true} onClose={() => {}} />

      <Box component="main" sx={{ flexGrow: 1, ml: { md: '70px' }, p: 3 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
            <Typography variant="h4" sx={{ color: '#4aa8b4' }}>
              Overzicht
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{ backgroundColor: '#4aa8b4' }}
              onClick={() => navigate('/rondes/nieuw')}
            >
              ronde
            </Button>
          </Box>

          <TextField
            fullWidth
            placeholder="Zoek rondes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ maxWidth: 400, mb: 3 }}
          />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <CircularProgress sx={{ color: '#4aa8b4' }} />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'naam'}
                          direction={sortDirection}
                          onClick={() => handleSort('naam')}
                        >
                          Naam
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Beschrijving</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredAndSortedData.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ color: '#4aa8b4', fontWeight: 'bold' }}>
                          <Button
                            onClick={() => handleRondeClick(r)}
                            sx={{ textTransform: 'none', padding: 0, color: '#4aa8b4' }}
                          >
                            {r.naam}
                          </Button>
                        </TableCell>
                        <TableCell>
                          {r.beschrijving || <em style={{ color: '#999' }}>Geen beschrijving</em>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>

                </Table>
              </TableContainer>

              <Box sx={{ textAlign: 'right', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredAndSortedData.length} van {rondes.length} rondes
                </Typography>
              </Box>
            </>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default Rondes;
